import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Flex, Spin } from "antd";
import clsx from 'clsx';
import { CalendarOutlined, CloseCircleOutlined, Loading3QuartersOutlined } from "@ant-design/icons";
import { useI18n } from "components/I18nProvider";
import { useEventAdminState } from "components/EventStateProvider";
import EventStatusBar from "components/EventStatusBar";
import { findSegment, isSegmentActivelyRecording, } from "lib/event-state-merge";
import { getSelectedSegmentStatusPresentation, shouldHideMainStatusSegmentTitle } from "lib/recording-status-presentation";
import { useDegradedSeconds } from "lib/use-degraded-seconds";
import { buildUrlPath } from "lib/resolveUrl";
import styles from './style.module.scss';
const EventCard = ({ access }) => {
    const { intl } = useI18n();
    const { eventSnapshot, eventLiveTelemetry, connectionReady, connectionHealth, selectedSegmentId, } = useEventAdminState();
    const [qrLoading, setQrLoading] = useState(true);
    const [statusTick, setStatusTick] = useState(0);
    const snapshotReady = access === 'ok' && eventSnapshot !== null;
    const eventId = snapshotReady ? eventSnapshot.id : null;
    const isSelectedSegmentRecording = isSegmentActivelyRecording(eventSnapshot, selectedSegmentId);
    const degradedSeconds = useDegradedSeconds(connectionHealth, connectionReady, isSelectedSegmentRecording);
    useEffect(() => {
        if (!isSelectedSegmentRecording) {
            return;
        }
        const id = window.setInterval(() => {
            setStatusTick((t) => t + 1);
        }, 1000);
        return () => {
            window.clearInterval(id);
        };
    }, [isSelectedSegmentRecording]);
    const selectedSegment = snapshotReady ? findSegment(eventSnapshot, selectedSegmentId) : null;
    const statusPresentation = useMemo(() => getSelectedSegmentStatusPresentation({
        connectionHealth,
        connectionReady,
        degradedSeconds,
        eventSnapshot: snapshotReady ? eventSnapshot : null,
        telemetry: eventLiveTelemetry,
        selectedSegmentId,
        selectedSegment,
        nowMs: Date.now(),
        intl,
    }), [
        connectionHealth,
        connectionReady,
        degradedSeconds,
        eventLiveTelemetry,
        eventSnapshot,
        intl,
        selectedSegment,
        selectedSegmentId,
        snapshotReady,
        statusTick,
    ]);
    const statusSegmentTitle = selectedSegmentId === null
        ? intl.formatMessage({ id: 'newSegment' })
        : selectedSegment?.title;
    const hideStatusSegmentTitle = shouldHideMainStatusSegmentTitle(connectionHealth, connectionReady, snapshotReady ? eventSnapshot : null, selectedSegmentId);
    useEffect(() => {
        setQrLoading(true);
    }, [eventId]);
    const icon = (() => {
        if (access === 'loading' || (access === 'ok' && eventSnapshot === null)) {
            return _jsx(Loading3QuartersOutlined, { className: styles.icon, spin: true });
        }
        if (access === 'not-found' || access === 'error') {
            return _jsx(CloseCircleOutlined, { className: styles.icon });
        }
        return _jsx(CalendarOutlined, { className: styles.icon });
    })();
    const heading = (() => {
        if (access === 'loading' || (access === 'ok' && eventSnapshot === null)) {
            return intl.formatMessage({ id: 'loading' });
        }
        if (access === 'not-found') {
            return intl.formatMessage({ id: 'eventNotFound' });
        }
        if (access === 'error') {
            return intl.formatMessage({ id: 'eventLoadFailed' });
        }
        return eventSnapshot.title;
    })();
    const body = snapshotReady ? (_jsxs("div", { children: [_jsx("p", { children: eventSnapshot.description ?? '' }), _jsx(Button, { target: "_blank", href: eventSnapshot.publicUrl, "test-id": "admin-event-subtitles-page-button", children: intl.formatMessage({ id: 'subtitlesPage' }) })] })) : null;
    return (_jsxs(_Fragment, { children: [_jsx(Card, { className: clsx(styles.eventCardDetails, !snapshotReady && styles.eventCardDetailsStandalone), children: _jsxs(Flex, { justify: "space-between", children: [_jsxs("div", { className: styles.eventInfo, children: [icon, _jsxs("div", { children: [_jsx("h2", { children: heading }), body] })] }), eventId !== null && (_jsx(Spin, { spinning: qrLoading, children: _jsx("div", { className: styles.qrCode, children: _jsx("img", { onLoad: () => setQrLoading(false), src: buildUrlPath(`/api/events/${eventId}/qrcode`), alt: "Event QR code" }) }) }))] }) }), snapshotReady && (_jsx("div", { className: styles.eventCardStatusSticky, children: _jsx(EventStatusBar, { led: statusPresentation.led, message: statusPresentation.message, segmentTitle: hideStatusSegmentTitle ? undefined : statusSegmentTitle, viewerCount: eventLiveTelemetry?.viewerCount }) }))] }));
};
export default EventCard;
